import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { propertyTemplates } from '../../../data/propertyTemplates';
import SocialProof from '../../../components/social-proof/SocialProof';

interface ListingTypeSelectorProps {
  onTemplateClick: () => void;
  onSingleListingClick: () => void;
  showTemplates: boolean;
  templateSectionRef: React.RefObject<HTMLDivElement>;
  onApplyTemplate: (templateId: string) => void;
  setShowTemplates: (show: boolean) => void;
}

/**
 * ListingTypeSelector Component
 * Displays the initial choice between template and single listings
 * Also handles template selection grid
 */
export const ListingTypeSelector: React.FC<ListingTypeSelectorProps> = ({
  onTemplateClick,
  onSingleListingClick,
  showTemplates,
  templateSectionRef,
  onApplyTemplate,
  setShowTemplates,
}) => {
  return (
    <motion.div
      key="listingTypeSelection"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className="space-y-6"
    >
      {/* Trust Badge & Headline */}
      <div className="text-center mb-8">
        <div className="mb-4">
          <span className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
            <CheckCircleIcon className="w-5 h-5 mr-2" />
            100% FREE • Get Listed in Under 5 Minutes
          </span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Choose Your Fastest Path to Success
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Over <strong>100 Irish landlords</strong> have found quality tenants across Ireland using
          RentSmart
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0 items-stretch">
        {/* Template Option Card - RECOMMENDED */}
        <div className="flex-1 relative">
          {/* Recommended Badge */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase shadow-lg">
              ⚡ Recommended
            </span>
          </div>
          <button
            type="button"
            onClick={onTemplateClick}
            className="w-full h-full bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex flex-col items-center text-center border-4 border-indigo-300 relative group"
          >
            <div className="bg-white bg-opacity-20 rounded-full p-4 mb-4 group-hover:bg-opacity-30 transition-all">
              <DocumentDuplicateIcon className="w-16 h-16 text-indigo-700" />
            </div>
            <span className="font-bold text-2xl mb-3">Start with a Template</span>
            <span className="text-indigo-100 text-xs font-semibold mb-3 bg-indigo-800 bg-opacity-50 px-3 py-1 rounded-full">
              ⏱️ 3x Faster Setup
            </span>
            <p className="text-sm px-2 mb-4 leading-relaxed">
              Pre-written descriptions, perfect formatting, and proven layouts. Just add your
              details and photos!
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-auto">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">Ready in 2 min</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">SEO Optimized</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">Higher Response</span>
              </div>
            </div>
          </button>
        </div>

        <div className="flex items-center justify-center md:w-auto w-full">
          <div className="bg-gray-100 rounded-full px-4 py-2">
            <span className="text-gray-700 font-medium text-sm">OR</span>
          </div>
        </div>

        {/* Single Listing Option - Custom Build */}
        <div className="flex-1">
          <button
            type="button"
            onClick={onSingleListingClick}
            className="w-full h-full bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-8 rounded-lg shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex flex-col items-center text-center group"
          >
            {/* Main Icon with Enhanced Styling */}
            <div className="bg-white bg-opacity-20 rounded-full p-4 mb-4 group-hover:bg-opacity-30 transition-all">
              <HomeIcon className="w-16 h-16 text-blue-700" />
            </div>
            {/* Listing Type Title */}
            <span className="font-bold text-2xl mb-3">Build from Scratch</span>
            <span className="text-blue-100 text-xs font-semibold mb-3 bg-blue-800 bg-opacity-50 px-3 py-1 rounded-full">
              ✏️ Full Control
            </span>
            {/* Description */}
            <p className="text-sm px-2 mb-4 leading-relaxed">
              Complete flexibility to customize every detail. Perfect for unique properties or
              experienced landlords.
            </p>
            {/* Feature Icons */}
            <div className="flex flex-wrap justify-center gap-3 mt-auto">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">Total Freedom</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">Custom Details</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">Your Style</span>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Template Selection Section */}
      {showTemplates && (
        <motion.div
          ref={templateSectionRef}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-8"
        >
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl shadow-2xl p-6 border-2 border-indigo-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <DocumentDuplicateIcon className="w-7 h-7 text-indigo-600 mr-2" />
                  Choose Your Template
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select a template that matches your property type
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTemplates(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(propertyTemplates).map(([templateId, template]) => (
                <button
                  type="button"
                  key={templateId}
                  onClick={() => onApplyTemplate(templateId)}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-indigo-500 hover:shadow-xl transition-all duration-300 bg-white text-left group relative overflow-hidden"
                >
                  {/* Hover Effect Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div className="relative flex flex-col space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="bg-indigo-100 p-2 rounded-lg group-hover:bg-indigo-200 transition-colors">
                          <DocumentDuplicateIcon className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 text-base group-hover:text-indigo-700 transition-colors">
                            {template.title}
                          </h4>
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {template.bedrooms} bed • {template.bathrooms} bath • {template.size}
                            m²
                          </p>
                        </div>
                      </div>
                      <div className="text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      <p className="line-clamp-2">{template.description.split('\n')[0]}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {template.amenities.slice(0, 3).map((amenity) => (
                        <span
                          key={amenity}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 group-hover:bg-indigo-200 transition-colors"
                        >
                          {amenity}
                        </span>
                      ))}
                      {template.amenities.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          +{template.amenities.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>
                    <strong>Pre-written</strong> descriptions
                  </span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>
                    <strong>SEO optimized</strong> for search
                  </span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span>
                    <strong>Proven</strong> to convert
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <SocialProof userType="landlord" />
    </motion.div>
  );
};

export default ListingTypeSelector;
